// @ts-nocheck There is an error with @atlaskit/dynamic-table props
import React, { MutableRefObject } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts';
import SectionMessage from '@atlaskit/section-message';

import { DataPointsEventsTotals, InterimResults } from '@/ui';
import ItemHeader from '@/components/primitive/item-header';
import {
  alignItemsStart,
  positionRelative,
  flex,
  flexDirectionColumn,
  spaceTopComfortable,
  card,
  spaceTopCozy,
  maxContentTooltip,
} from '@/styles';
import { selectedDateLabels } from '@/utils/date';
import { localNumberFormat } from '@/utils/string';
import { getPreviousDay } from '@/controllers/slo-graph-controller/utils';

import { formatPerformanceResult, formatErrorBudget, getDailyPerformance } from '../utils';

import EventsList from './tooltip/events-list';
import NoResultsAvailable from './tooltip/no-result-available';
import LoadingInterimResults from './tooltip/loading-interim-results';
import RightAlign from './tooltip/right-align';
import IncidentsList from './tooltip/incidents-list';
import { lowEmphasisText, tooltipBackground, tooltipResults } from './tooltip/styles';

export type CustomToolTipProps = TooltipProps<any, any> & {
  sticky?: boolean;
  showClickPrompt?: boolean;
  graphEventSource?: GraphEventSource;
};

const CustomTooltip = ({
  active,
  payload = [],
  showClickPrompt,
  graphEventSource,
}: CustomToolTipProps) => {
  if (active && payload && payload.length) {
    const {
      result,
      rollup,
      timestamp,
      goodCount,
      badCount,
      dailyGood,
      dailyBad,
      events: allEvents = [],
      incidents = [],
      isHighCardinality,
      interimResults,
    } = payload[0].payload as DataPointsEventsTotals & {
      rollup: number;
      interimResults: InterimResults;
    };

    const dailyPerformance = getDailyPerformance(dailyGood, dailyBad);
    const hasEventCounts = goodCount !== undefined || badCount !== undefined;
    const incidentsCount = incidents.length;

    const hasInterimErrorOrNoData =
      interimResults?.error ||
      (interimResults?.isLoading === false && interimResults?.timestamp === undefined);

    // eslint-disable-next-line prefer-const
    let { timestampLabel, rangeLabel } = interimResults?.timestamp
      ? selectedDateLabels(new Date(interimResults.timestamp), true)
      : selectedDateLabels(timestamp);

    if (interimResults?.isLoading || hasInterimErrorOrNoData) {
      const { timestampLabel: currentDayLabel } = selectedDateLabels(new Date());
      if (interimResults?.isMissingPreviousDayResults) {
        const { timestampLabel: previousDayLabel } = selectedDateLabels(getPreviousDay(new Date()));
        timestampLabel = `${previousDayLabel} & ${currentDayLabel}`;
      } else {
        timestampLabel = currentDayLabel;
      }
    }

    return (
      <div
        css={[positionRelative, flex, flexDirectionColumn, alignItemsStart, maxContentTooltip]}
        onClick={(e: any) => e.stopPropagation()}
      >
        {hasInterimErrorOrNoData && (
          <div css={[tooltipBackground, card]}>
            <h4>{timestampLabel}</h4>
            <div css={[tooltipResults]}>
              <NoResultsAvailable />
            </div>
          </div>
        )}
        {hasEventCounts && !hasInterimErrorOrNoData && (
          <div css={[tooltipBackground, card]}>
            <h4>{timestampLabel}</h4>
            <div css={[tooltipResults]}>
              {goodCount === undefined &&
                badCount === undefined &&
                (interimResults?.isLoading ? <LoadingInterimResults /> : <NoResultsAvailable />)}
              {(result || result === 0) && (
                <>
                  <ItemHeader>Performance</ItemHeader>
                  <RightAlign>{formatPerformanceResult(dailyPerformance)}%</RightAlign>
                  <ItemHeader>Good Events</ItemHeader>
                  <RightAlign>{localNumberFormat(dailyGood)}</RightAlign>
                  <ItemHeader>Bad Events</ItemHeader>
                  <RightAlign>{localNumberFormat(dailyBad)}</RightAlign>
                </>
              )}
            </div>
            <h4>{rangeLabel}</h4>
            <div css={[tooltipResults]}>
              <ItemHeader>Performance</ItemHeader>
              <RightAlign>{formatPerformanceResult(rollup)}%</RightAlign>
              <ItemHeader>Error Budget</ItemHeader>
              <RightAlign>{formatErrorBudget(result, { truncate: false })}%</RightAlign>
              <ItemHeader>Good Events</ItemHeader>
              <RightAlign>{localNumberFormat(goodCount)}</RightAlign>
              <ItemHeader>Bad Events</ItemHeader>
              <RightAlign>{localNumberFormat(badCount)}</RightAlign>
            </div>

            <IncidentsList incidentsCount={incidentsCount} />
            <EventsList graphEventSource={graphEventSource} events={allEvents} />
            {isHighCardinality && !!allEvents.length && (
              <div css={[spaceTopCozy]}>
                <SectionMessage appearance="warning" actions={[]}>
                  <p>High Cardinality Metrics</p>
                </SectionMessage>
              </div>
            )}
            {showClickPrompt && allEvents.length > 0 && (
              <div css={[spaceTopComfortable]}>
                <p css={[lowEmphasisText]}>Click the graph to view event details</p>
              </div>
            )}
          </div>
        )}
        {!hasInterimErrorOrNoData && !hasEventCounts && (
          <div css={[tooltipBackground, card]}>
            <h4>{timestampLabel}</h4>
            <div css={[tooltipResults]}>
              {goodCount === undefined &&
                badCount === undefined &&
                (interimResults?.isLoading ? <LoadingInterimResults /> : <NoResultsAvailable />)}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export type StickyCustomToolTipProps = CustomToolTipProps & {
  stickyPayloadRef: MutableRefObject<Array<Payload<number, string>>>;
  graphEventSource?: GraphEventSource;
};

// This tooltip wrapper caches the last selected payload to a provided
// reference. This can be read to determine which day of the graph is selected
// when clicked.
// If sticky is set to true, the tooltip is rendered with a snapshot of the
// last captured payload value until sticky is unset.
export const StickyCustomTooltip = ({
  active,
  payload = [],
  showClickPrompt,
  sticky = undefined,
  stickyPayloadRef,
  graphEventSource,
}: StickyCustomToolTipProps) => {
  stickyPayloadRef.current = payload;

  if (sticky !== undefined && !sticky) {
    stickyPayloadRef.current = payload;
    return (
      <CustomTooltip
        active={active}
        payload={payload}
        showClickPrompt={showClickPrompt}
        graphEventSource={graphEventSource}
      />
    );
  }
  return (
    <CustomTooltip
      active
      payload={stickyPayloadRef.current}
      sticky={sticky}
      showClickPrompt={showClickPrompt}
      graphEventSource={graphEventSource}
    />
  );
};

export default CustomTooltip;
